import { qrCodeTypesLabelMap } from '@back-office/http';
import { GcpBox, GcpTypography, getDatetimeText } from '@back-office/ui';
import { Divider } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { useTheme } from '@material-ui/core/styles';
import TextFields from '@material-ui/core/TextField';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import BorderColor from '@material-ui/icons/BorderColor';
import clsx from 'clsx';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import useCardStyles, { CustomTooltip } from '../styles';
import useViewModel from './viewmodel';

interface TransactionDetailCardProps {
  transactionID: string;
  id: string;
  qrType: string;
  ref1: string;
  ref2: string;
  createdAt: string | null;
  description: string | null;
  txId: string;
  status: string;
  refresh: () => void;
  onUpdated: () => void;
}

export type PopupState = {
  show: boolean;
  state: boolean;
};

const TransactionDetailCard: FC<TransactionDetailCardProps> = (props) => {
  // const { alert, auth } = useInject((store) => store);
  const classes = useCardStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('lg'));
  const {
    transactionID,
    id,
    ref1,
    ref2,
    createdAt,
    description,
    txId,
    qrType,
    status,
    refresh,
    onUpdated,
  } = props;

  const {
    handleOnChange,
    handleSave,
    enableSaveEdited,
    handleOnClickEdit,
    showEditRef2,
    isStaticQrTransaction,
    editedRef2,
  } = useViewModel({
    txId,
    refresh,
    onUpdated,
    ref2,
  });

  return (
    <GcpBox className={classes.card} mt={2}>
      <GcpBox display="flex" pb={3} pl={3} pt={3}>
        <GcpTypography variant="body2">
          {t('transaction_list.detail')}
        </GcpTypography>
      </GcpBox>
      <Divider />
      <GcpBox mb={3} mt={3} pb={3} pl={3}>
        <Grid container className={classes.wrap} spacing={matches ? 2 : 0}>
          <Grid container item lg={6} spacing={matches ? 0 : 2} xs={12}>
            <Grid item lg={6} xs={4}>
              {t('transaction_list.id')}
            </Grid>
            <Grid item className={classes.uuid} lg={6} xs={8}>
              <GcpTypography paint="hitGray" variant="body1">
                {id || '-'}
              </GcpTypography>
            </Grid>
            <Grid item lg={6} xs={4}>
              {t('transaction_list.merchant_id')}
            </Grid>
            <Grid item lg={6} xs={8}>
              <GcpTypography paint="hitGray" variant="body1">
                {transactionID || '-'}
              </GcpTypography>
            </Grid>
            <Grid
              item
              className={clsx({ [classes.secondColumn]: matches })}
              lg={6}
              xs={4}
            >
              {t('transaction_list.created')}
            </Grid>
            <Grid
              item
              className={clsx({ [classes.secondColumn]: matches })}
              lg={6}
              xs={8}
            >
              <GcpTypography paint="hitGray" variant="body1">
                {createdAt ? getDatetimeText(createdAt, 'default') : '-'}
              </GcpTypography>
            </Grid>
            <Grid
              item
              className={clsx({ [classes.secondColumn]: matches })}
              lg={6}
              xs={4}
            >
              {t('transaction_list.qr_type')}
            </Grid>
            <Grid
              item
              className={clsx({ [classes.secondColumn]: matches })}
              lg={6}
              xs={8}
            >
              <GcpTypography paint="hitGray" variant="body1">
                {qrType
                  ? t(
                      `transaction_list.qr_types.${qrCodeTypesLabelMap[qrType]}`
                    )
                  : 'N/A'}
              </GcpTypography>
            </Grid>
          </Grid>
          <Grid
            container
            item
            className={clsx({ [classes.divider]: !matches })}
            lg={6}
            spacing={matches ? 0 : 2}
            xs={12}
          >
            <Grid item lg={3} xs={4}>
              {t('transaction_list.ref1')}
            </Grid>
            <Grid item lg={9} xs={8}>
              <GcpTypography paint="hitGray" variant="body1">
                {ref1 || '-'}
              </GcpTypography>
            </Grid>
            <Grid
              item
              className={clsx({ [classes.secondColumn]: matches })}
              lg={3}
              xs={4}
            >
              {t('transaction_list.ref2')}
            </Grid>
            <Grid
              item
              className={clsx({ [classes.secondColumn]: matches })}
              direction="row"
              lg={9}
              xs={8}
            >
              <GcpBox display="flex" mr={1}>
                {showEditRef2.get(ref2) ? (
                  <>
                    <TextFields
                      size="small"
                      type="text"
                      value={editedRef2}
                      variant="outlined"
                      onChange={handleOnChange}
                    />
                    <Button
                      className={clsx(classes.secondaryButton)}
                      disabled={!enableSaveEdited}
                      variant="outlined"
                      onClick={() => handleSave()}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <CustomTooltip placement="bottom-end" title={ref2 || '-'}>
                      <div>
                        <GcpTypography
                          className={clsx(classes.ref2Message)}
                          paint="hitGray"
                          variant="body1"
                        >
                          {ref2 || '-'}
                        </GcpTypography>
                      </div>
                    </CustomTooltip>
                    {status === 'authorized' && (
                      <Button
                        className={clsx(
                          classes.secondaryButton,
                          classes.editButton,
                          {
                            [classes.hidden]: !isStaticQrTransaction(qrType),
                          }
                        )}
                        variant="outlined"
                        onClick={() => handleOnClickEdit()}
                      >
                        <BorderColor fontSize="small" />
                      </Button>
                    )}
                  </>
                )}
              </GcpBox>
            </Grid>
            <Grid
              item
              className={clsx({
                [classes.secondColumn]: matches,
                [classes.lastColumn]: matches,
              })}
              lg={3}
              xs={4}
            >
              {t('transaction_list.desc')}
            </Grid>
            <Grid
              item
              className={clsx({ [classes.secondColumn]: matches })}
              lg={9}
              xs={8}
            >
              <GcpTypography paint="hitGray" variant="body1">
                {description || '-'}
              </GcpTypography>
            </Grid>
          </Grid>
        </Grid>
      </GcpBox>
    </GcpBox>
  );
};

export default TransactionDetailCard;
